import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Mail, Network, Cpu, Library, Handshake, Lock } from "lucide-react";
import { BrandLockup, MythosBrand } from "@/components/Brand";

const tldr = [
    ["Company", (
        <a
            key="tldr-company"
            href="https://mythosindiastudio.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tldr-company-link"
            className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary"
        >
            Mythos India Studios Private Limited ↗
        </a>
    )],
    ["CIN", "U62010TS2026PTC221089"],
    ["Platform", "LifeVault — AI life-OS for the Indian household (live)"],
    ["Next platform", "EduSphere — AI-first immersive learning (in build)"],
    ["Stage", "Pre-seed → Series A · revenue-ready"],
    ["Model", "B2B2C · advisor SaaS + DTC freemium"],
    ["ARPU", "₹500–2,000 / client / year"],
    ["TAM", "$7.8T Indian household wealth · 10M+ HNI households"],
    ["Asks", "Strategic capital + advisor partnerships"],
];

const traction = [
    { v: "11", l: "production modules" },
    { v: "5", l: "live AI surfaces" },
    { v: "₹500–2k", l: "ARPU advisor / yr" },
    { v: "0 CAC", l: "advisor-led GTM" },
    { v: "Sonnet 4.5", l: "AI grounding" },
    { v: "SOC-grade", l: "encryption + 2FA + audit" },
];

const useOfFunds = [
    { icon: Network, t: "Advisor BD", b: "Onboard the first 1,000 advisors at zero CAC. ₹240 Cr ARR ceiling unlocked at 10k." },
    { icon: Cpu, t: "AI depth", b: "Heritage interview agent, vision-OCR pipeline, Hindi/Tamil/Telugu LLM tuning." },
    { icon: Library, t: "EduSphere", b: "Ship the second platform sharing infra and brand. Compounds LTV/household." },
    { icon: Lock, t: "Trust ops", b: "SOC2, RBI-grade audit, regulatory licensing for adjacent advisory features." },
];

const competitors = [
    ["Document Storage (Google/Dropbox)", "✓", "✓"],
    ["Investment Tracking (INDmoney/Kuvera)", "✓", "✓"],
    ["Net-worth Aggregator (1Finance)", "✓", "✓"],
    ["AI grounded in private vault", "—", "✓"],
    ["Heritage / Stories module", "—", "✓"],
    ["Crisis Mode (PIN-gated, QR-shared)", "—", "✓"],
    ["Advisor + Family integration", "—", "✓"],
    ["Generational hand-over protocol", "—", "✓"],
];

export default function Investors() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border/70 sticky top-0 z-30 bg-background/80 backdrop-blur-md">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2" data-testid="inv-back">
                        <ArrowLeft className="h-4 w-4" /> Back to product
                    </Link>
                    <BrandLockup size="sm" parent={true} />
                    <a
                        href="https://mythosindiastudio.com/contact"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="inv-cta-top"
                        className="text-sm bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 font-semibold inline-flex items-center gap-2"
                    >
                        <Mail className="h-4 w-4" /> Talk to founders ↗
                    </a>
                </div>
            </header>

            <section className="aurora grain border-b border-border/70 relative overflow-hidden">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 py-20 lg:py-28 relative z-10">
                    <div className="label-tag mb-4 inline-flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Investor brief · Confidential preview
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] max-w-4xl">
                        We are the operating system
                        <br />
                        for India's <span className="text-primary italic">$7.8T</span> household wealth —
                        <br />
                        <span className="opacity-70">and we're just getting started.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-3xl leading-relaxed">
                        LifeVault is live. EduSphere is in build. MYTHIVA™ is the parent
                        studio compounding both. This page is a one-pager for institutional investors
                        and strategic partners exploring the round.
                    </p>
                </div>
            </section>

            {/* TL;DR */}
            <section className="py-16 lg:py-20 border-b border-border/70">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="label-tag mb-3">TL;DR</div>
                        <h2 className="font-display text-3xl font-bold tracking-tight">
                            For partners who skim first.
                        </h2>
                    </div>
                    <dl className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-5" data-testid="inv-tldr">
                        {tldr.map(([k, v]) => (
                            <div key={k}>
                                <dt className="label-tag">{k}</dt>
                                <dd className="mt-1 text-sm font-semibold leading-snug">{v}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            {/* Traction strip */}
            <section className="py-12 border-b border-border/70 bg-card/30">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-testid="inv-traction">
                    {traction.map((t) => (
                        <div key={t.l}>
                            <div className="font-display text-2xl lg:text-3xl font-extrabold font-mono tracking-tight text-primary">{t.v}</div>
                            <div className="mt-1 label-tag">{t.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* The thesis */}
            <section className="py-20 lg:py-24">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="label-tag mb-3">Thesis</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                            India is mid-handover.<br />
                            <span className="italic opacity-70">No one has built the rails.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-4 text-base text-foreground/90 leading-relaxed">
                        <p>
                            Over the next 25 years, <strong>₹128 lakh-crore</strong> in Indian household
                            wealth will move from one generation to the next. Today, that wealth lives in
                            paper files, fragmented apps, and the heads of busy patriarchs. There is no
                            unified operating system for it.
                        </p>
                        <p>
                            LifeVault is that OS — documents, finances, heritage, nominees and advisors
                            in a single inheritable graph, AI-grounded in each household's own data,
                            crisis-mode-shareable to the next of kin. We monetise advisors (B2B2C) and
                            keep the family layer free.
                        </p>
                        <p>
                            <strong>Why now:</strong> consumer-grade AI just crossed the threshold to
                            read a Will, score a vault, and surface a missing nominee — at family-affordable
                            cost. The window is narrow, and entirely open.
                        </p>
                    </div>
                </div>
            </section>

            {/* Competitor matrix */}
            <section className="py-20 lg:py-24 border-y border-border/70 bg-card/30">
                <div className="mx-auto max-w-5xl px-6 sm:px-8">
                    <div className="label-tag mb-3">Competitive landscape</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-8">
                        We are not another finance app.
                    </h2>
                    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="inv-compare">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-secondary/60">
                                    <th className="text-left px-5 py-3 label-tag">Capability</th>
                                    <th className="text-center px-5 py-3 label-tag">Existing apps</th>
                                    <th className="text-center px-5 py-3 label-tag text-primary">LifeVault</th>
                                </tr>
                            </thead>
                            <tbody>
                                {competitors.map(([cap, a, b], i) => (
                                    <tr key={cap} className={i % 2 ? "bg-card" : "bg-card/60"}>
                                        <td className="px-5 py-3">{cap}</td>
                                        <td className="px-5 py-3 text-center font-mono text-muted-foreground">{a}</td>
                                        <td className="px-5 py-3 text-center font-mono text-primary font-bold">{b}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Use of funds */}
            <section className="py-20 lg:py-24">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 mb-10">
                        <div className="lg:col-span-5">
                            <div className="label-tag mb-3">Use of funds</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
                                Compound the graph.<br />
                                <span className="italic opacity-70">Ship EduSphere.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-7 self-end text-muted-foreground leading-relaxed">
                            Capital deployed across four vectors that compound household LTV and widen
                            distribution at zero CAC.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {useOfFunds.map((u) => (
                            <article key={u.t} className="rounded-lg border border-border bg-card p-6" data-testid={`inv-fund-${u.t.replace(/\s+/g, '-').toLowerCase()}`}>
                                <u.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-4 font-display text-lg font-semibold">{u.t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{u.b}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Roadmap */}
            <section className="py-20 lg:py-24 border-y border-border/70 bg-card/30">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="label-tag mb-3">Roadmap</div>
                    <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mb-12">
                        From vault → studio → household OS.
                    </h2>
                    <ol className="grid md:grid-cols-3 gap-6">
                        {[
                            ["Now · live", "LifeVault MVP", "11 modules, 5 AI surfaces, advisor billing, Sanctuary share, 2FA + audit."],
                            ["Next 6 mo", "Series-A scale", "1,000 paying advisors, OCR pipeline depth, Hindi/Tamil/Telugu, heritage interview agent."],
                            ["12–24 mo", "EduSphere launch", "Same households, learning vertical. Cross-platform Mythos OS — wealth + learning under one brand."],
                        ].map(([when, t, b], i) => (
                            <li key={t} className="rounded-lg border border-border bg-card p-6" data-testid={`inv-roadmap-${i}`}>
                                <div className="label-tag text-primary">{when}</div>
                                <h3 className="mt-3 font-display text-xl font-semibold">{t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* The ask */}
            <section className="py-24 lg:py-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/15 via-primary/5 to-transparent pointer-events-none" />
                <div className="mx-auto max-w-5xl px-6 sm:px-8 relative">
                    <div className="label-tag mb-3">The ask</div>
                    <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
                        Lead investors,<br />
                        <span className="italic opacity-70">strategic partners,</span>
                        <br />
                        <span className="text-primary">family offices —</span>
                        <br />
                        we'd like to talk.
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                        We're raising to compound the data graph, scale advisor BD, and ship EduSphere.
                        Open to lead and select co-investors. Decks, dataroom and KPIs available on request.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href="https://mythosindiastudio.com/contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inv-cta-email"
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 rounded-full text-sm font-semibold hover:bg-primary/90"
                        >
                            <Mail className="h-4 w-4" /> Contact founders ↗
                        </a>
                        <a
                            href="https://mythosindiastudio.com/contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inv-cta-partner"
                            className="inline-flex items-center gap-2 border border-border bg-card px-7 py-4 rounded-full text-sm font-semibold hover:bg-secondary"
                        >
                            <Handshake className="h-4 w-4" /> Advisor partnership ↗
                        </a>
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold text-foreground/80 hover:text-foreground"
                        >
                            Or try the product <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <p className="mt-10 text-xs text-muted-foreground max-w-3xl leading-relaxed">
                        This page is a forward-looking summary intended for institutional and accredited
                        investors. It does not constitute an offer to sell securities. Specific terms,
                        valuation, and dataroom access are shared under NDA. MYTHIVA™ Private
                        Limited reserves the right to update the terms of any contemplated round.
                    </p>
                </div>
            </section>

            <footer className="py-10 text-sm text-muted-foreground border-t border-border/70" data-testid="investors-footer">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <MythosBrand size="sm" showCIN={true} testid="investors-mythos-brand" />
                    <div className="text-xs text-center md:text-right leading-relaxed">
                        © {new Date().getFullYear()}{" "}
                        <a
                            href="https://mythosindiastudio.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="investors-footer-mythos-link"
                            className="underline hover:text-foreground"
                        >
                            Mythos India Studios Private Limited ↗
                        </a>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground/80">
                            CIN U62010TS2026PTC221089
                        </div>
                    </div>
                    <Link to="/" className="text-xs underline hover:text-foreground">← Product</Link>
                </div>
            </footer>
        </div>
    );
}
